import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Button, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDialog from './ConfirmDialog';
import { layout } from '../../themes/smartinventoryTheme';

// The coupon / advertisement wizard chrome: thin progress bars + labels
// (clickable to jump back to a completed step), Back/Next in the footer,
// the final step's primary reads e.g. "Create coupon". Closing while dirty
// reuses ConfirmDialog rather than a second bespoke confirm — never build a
// second one.
const WizardDrawer = ({
  open,
  onClose,
  title,
  steps,
  activeStep,
  onStepClick,
  closeLabel,
  backLabel,
  nextLabel,
  finalLabel,
  onBack,
  onNext,
  onFinish,
  nextDisabled,
  isDirty,
  discardConfirm, // { title, description, cancelLabel, confirmLabel } — shown when closing dirty
  children,
}) => {
  const [confirmingClose, setConfirmingClose] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const requestClose = () => {
    if (isDirty && discardConfirm) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={requestClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: layout.drawerWidthWide }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, pb: 1.5 }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {steps[activeStep]?.label}
            </Typography>
          </Box>
          <IconButton onClick={requestClose} aria-label={closeLabel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, px: 2.5, pb: 2 }}>
          {steps.map((step, index) => (
            <Box
              key={step.label}
              onClick={() => index < activeStep && onStepClick && onStepClick(index)}
              sx={{ flex: 1, cursor: index < activeStep ? 'pointer' : 'default' }}
            >
              <LinearProgress
                variant="determinate"
                value={index <= activeStep ? 100 : 0}
                color={index <= activeStep ? 'primary' : undefined}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5 }}>
          {children}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="text" onClick={onBack} disabled={activeStep === 0}>{backLabel}</Button>
          <Button variant="contained" onClick={isLastStep ? onFinish : onNext} disabled={nextDisabled}>
            {isLastStep ? finalLabel : nextLabel}
          </Button>
        </Box>
      </Drawer>

      {discardConfirm && (
        <ConfirmDialog
          open={confirmingClose}
          onClose={() => setConfirmingClose(false)}
          title={discardConfirm.title}
          description={discardConfirm.description}
          cancelLabel={discardConfirm.cancelLabel}
          confirmLabel={discardConfirm.confirmLabel}
          onConfirm={() => {
            setConfirmingClose(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default WizardDrawer;
